import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import { lightTheme } from "../../../../theme"
import { SERIES_COLORS } from "../../charts/palette"
import TrendLineChart from "../../charts/TrendLineChart"
import ChangeBadge from "../../components/Badges/ChangeBadge"
import KpiStrip from "../../components/KpiStrip/KpiStrip"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { useCardData } from "../../hooks/useCardData"
import { getPercentChange, getPointChange, GOOD_DIRECTION } from "../../utils/change"
import { formatCount, formatMoney, formatShare } from "../../utils/formats"

// The card's Tooltip terms, verbatim from the spec (§6 Average value).
const INFO = [
  {
    name: "Revenue per merchant",
    formula: "Revenue ÷ merchants with at least one charge in the period"
  },
  { name: "Average lookup price", formula: "Revenue ÷ paid lookups" },
  { name: "Call center share", formula: "Call center revenue ÷ all revenue × 100" }
]

// The price trend's values and y-axis: "$" with two decimals (DAA-6-12).
const formatPrice = (value) => formatMoney(value, 2)

// The share trend's y-axis reads whole percents, its hover and last-value label one decimal (DAA-6-12).
const formatWholeShare = (value) => formatShare(value, 0)

// Lower than the page's other charts (260), as the design draws these trends: a 120px plot plus the
// x-axis labels.
const CHART_HEIGHT = 160

const hasNonZero = (values) => values?.some((value) => value > 0)

// Average value (DAA-6-09 … DAA-6-12): what an average paying merchant and an average lookup bring
// in, and how much of the money comes from the call center — the three averages and the weekly
// trends of the average price and the call center share. The merchant tag filters do not reach it
// (DAA-2-27).
const AverageValueCard = () => {
  const { t } = useTranslation()
  const { data, loading } = useCardData("averageValue")
  const kpis = data?.kpis
  const comparison = data?.comparison

  const tiles = useMemo(() => {
    // Change badges only while comparing (DAA-2-22).
    const percentBadge = (value, previous) =>
      comparison ? (
        <ChangeBadge change={getPercentChange(value, previous, GOOD_DIRECTION.UP)} />
      ) : null

    return [
      {
        key: "revenuePerMerchant",
        label: t("Revenue per paying merchant"),
        value: kpis?.revenue_per_merchant,
        prefix: "$",
        badge: percentBadge(kpis?.revenue_per_merchant, comparison?.revenue_per_merchant),
        secondary: comparison
          ? `vs ${formatMoney(comparison.revenue_per_merchant, 0)} ${t("prev. period")}`
          : null,
        note: kpis ? `${formatCount(kpis.paying_merchants)} ${t("paying merchants")}` : null
      },
      {
        key: "averagePrice",
        label: t("Average lookup price"),
        value: kpis?.average_price,
        prefix: "$",
        decimals: 2,
        badge: percentBadge(kpis?.average_price, comparison?.average_price),
        secondary: comparison
          ? `vs ${formatPrice(comparison.average_price)} ${t("prev. period")}`
          : null
      },
      {
        key: "callCenterShare",
        label: t("Call center share of revenue"),
        value: kpis?.call_center_share,
        decimals: 1,
        suffix: "%",
        // In pp, and neither way is good (DAA-6-11).
        badge: comparison ? (
          <ChangeBadge
            change={getPointChange(
              kpis?.call_center_share,
              comparison.call_center_share,
              GOOD_DIRECTION.NEUTRAL
            )}
          />
        ) : null,
        secondary: comparison
          ? `vs ${formatShare(comparison.call_center_share)} ${t("prev. period")}`
          : null
      }
    ]
  }, [kpis, comparison, t])

  // A trend that is 0 every week — no paid lookups, or no call center revenue — reads "No data
  // available", as the Revenue charts above it (DAA-1-18).
  const priceSeries = useMemo(() => {
    if (!hasNonZero(data?.trend.average_price)) return []

    return [
      {
        key: "averagePrice",
        // The hover row's name, as the design writes it.
        label: t("Avg price"),
        color: lightTheme.token.colorPrimary,
        data: data.trend.average_price
      }
    ]
  }, [data, t])
  const shareSeries = useMemo(() => {
    if (!hasNonZero(data?.trend.call_center_share)) return []

    return [
      {
        key: "callCenterShare",
        label: t("Call center share"),
        color: SERIES_COLORS.call_center,
        data: data.trend.call_center_share
      }
    ]
  }, [data, t])

  return (
    <MetricCard
      title={t("Average value")}
      info={INFO}
      notAffectedBy={t("Not affected by: User email, User full name, Business account")}
      loading={loading}
    >
      <KpiStrip tiles={tiles} />
      {/* Both trends are weekly at every period length (DAA-6-12). Panels of 4 and 8, so their divider
          continues the first KPI divider (DAA-1-21). They keep their place until the first answer, so
          the page does not jump under them. */}
      <PanelRow>
        <Panel title={t("Average lookup price — trend")} tag={t("weekly")} span={4}>
          <div style={{ height: CHART_HEIGHT }}>
            {data && (
              <TrendLineChart
                labels={data.labels}
                series={priceSeries}
                lastIntervalIncomplete={data.last_interval_incomplete}
                yFormat={formatPrice}
                zoomY
                yMinStep={0.01}
                // A lone line gets its area, as the design draws it.
                fill
                height={CHART_HEIGHT}
              />
            )}
          </div>
        </Panel>
        <Panel title={t("Call center share — trend")} tag={t("weekly")} span={8}>
          <div style={{ height: CHART_HEIGHT }}>
            {data && (
              <TrendLineChart
                labels={data.labels}
                series={shareSeries}
                lastIntervalIncomplete={data.last_interval_incomplete}
                yFormat={formatShare}
                yTickFormat={formatWholeShare}
                zoomY
                yMax={100}
                yMinStep={1}
                // A lone line gets its area, as the design draws it.
                fill
                height={CHART_HEIGHT}
              />
            )}
          </div>
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default AverageValueCard
